import React, { createContext, useContext, useMemo, type Dispatch, type SetStateAction } from 'react'

import type { UseFieldArrayReturn } from 'react-hook-form'
import { useFormContext, useWatch } from 'react-hook-form'

import { EmpreendimentoFactory } from '@/domain/simulador/factories/empreendimento-factory'
import { Empreendimento } from '@/features/simulador/brasil/domain/empreendimento'
import type { TSimuladorSchema, TSimuladorSchemaInput } from '@/features/simulador/brasil/types/simulacao.type'

interface IEmpreendimentoContextValue {
  empreendimento: Empreendimento
  indexEmpreendimento: number
  fieldArraySimulacoes: UseFieldArrayReturn<TSimuladorSchemaInput, `empreendimentos.${number}.simulacoes`>
  activeSimulacaoIndex: number
  setActiveSimulacaoIndex: Dispatch<SetStateAction<number>>
}

interface IEmpreendimentoProvider {
  children: React.ReactNode
  indexEmpreendimento: number
  fieldArraySimulacoes: UseFieldArrayReturn<TSimuladorSchemaInput, `empreendimentos.${number}.simulacoes`>
  activeSimulacaoIndex: number
  setActiveSimulacaoIndex: Dispatch<SetStateAction<number>>
}

const EmpreendimentoContext = createContext<IEmpreendimentoContextValue | null>(null)

export const useEmpreendimentoContext = () => {
  const context = useContext(EmpreendimentoContext)
  if (!context) {
    throw new Error('useEmpreendimentoContext must be used within EmpreendimentoProvider')
  }
  return context
}

export const EmpreendimentoProvider = ({
  children,
  indexEmpreendimento,
  fieldArraySimulacoes,
  activeSimulacaoIndex,
  setActiveSimulacaoIndex,
}: IEmpreendimentoProvider) => {
  const { control } = useFormContext<TSimuladorSchema>()

  const empreendimentoData = useWatch({
    control,
    name: `empreendimentos.${indexEmpreendimento}`,
  })
  const tipoDoProduto = useWatch({
    control,
    name: 'tipoDoProduto',
  })
  const empreendimento = EmpreendimentoFactory.fromFormData(empreendimentoData, tipoDoProduto)

  const contextValue: IEmpreendimentoContextValue = useMemo(
    () => ({
      empreendimento,
      indexEmpreendimento,
      fieldArraySimulacoes,
      activeSimulacaoIndex,
      setActiveSimulacaoIndex,
    }),
    [empreendimento, indexEmpreendimento, fieldArraySimulacoes, activeSimulacaoIndex, setActiveSimulacaoIndex],
  )

  return <EmpreendimentoContext.Provider value={contextValue}>{children}</EmpreendimentoContext.Provider>
}
